/**
 * parseCSV — proper CSV parser that handles quoted fields, embedded commas,
 * and line breaks within cell values. Split-by-comma breaks on CSVs with
 * field values like "You can feel the difference..." because naive
 * `split(',')` treats the commas inside the quoted body text as column
 * separators.
 *
 * Returns an array of objects keyed by the normalized (lowercased,
 * whitespace-trimmed) header names from row 0.
 */

export function parseCSV(raw: string): Record<string, string>[] {
  const lines = raw
    .trim()
    .split('\n')
    .filter((l) => l.trim().length > 0)

  if (lines.length < 2) return []

  const headers = parseCSVLine(lines[0]).map((h) => h.trim().toLowerCase())

  const rows: Record<string, string>[] = []
  for (let i = 1; i < lines.length; i++) {
    const cols = parseCSVLine(lines[i])
    const row: Record<string, string> = {}
    for (let j = 0; j < headers.length; j++) {
      row[headers[j]] = (cols[j] ?? '').trim()
    }
    rows.push(row)
  }

  return rows
}

/**
 * Parse a single CSV line into fields.
 *
 * Handles:
 *   - Double-quoted fields: `"hello, world"`
 *   - Embedded quotes doubled in the CSV: `"value with ""quote"""` → value with "quote"
 *   - Unquoted fields trimmed of leading/trailing whitespace
 *
 * This is a full RFC 4180 parser for individual lines. Multi-line
 * fields (where a quoted value spans multiple lines of the input) are
 * NOT handled — the caller must pre-join those before calling. That's
 * fine for our admin CSV import use case where multi-line body text is
 * trimmed to a single line in the CSV.
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = []
  let current = ''
  let inQuotes = false

  for (let i = 0; i < line.length; i++) {
    const char = line[i]

    if (inQuotes) {
      if (char === '"') {
        // Peek ahead: "" inside quotes is an escaped quote
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"'
          i++ // skip the second quote
        } else {
          inQuotes = false
        }
      } else {
        current += char
      }
    } else {
      if (char === '"') {
        inQuotes = true
      } else if (char === ',') {
        result.push(current)
        current = ''
      } else {
        current += char
      }
    }
  }
  result.push(current)

  return result
}
